/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { platformApi } from './api';
import type { AssignedPod, AssignedHelmRelease, PodSummary } from '../types/assignment';

const BASE = '/assignments';

function wsUrlFor(path: string): string {
  // platformApi.defaults.baseURL is like https://internal.podstack.ai/platform/
  const httpBase = (platformApi.defaults.baseURL || window.location.origin).replace(/\/$/, '');
  const wsBase = httpBase.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
  const token = localStorage.getItem('token') || '';
  const sep = path.includes('?') ? '&' : '?';
  return `${wsBase}${path}${sep}token=${encodeURIComponent(token)}`;
}

export const assignmentApi = {
  listPods: () =>
    platformApi
      .get<{ data: AssignedPod[]; meta: { total: number } }>(`${BASE}/pods`)
      .then((r) => r.data),

  getPod: (id: string) =>
    platformApi
      .get<{ data: AssignedPod; live: any }>(`${BASE}/pods/${id}`)
      .then((r) => r.data),

  restartPod: (id: string) =>
    platformApi
      .post<{ data: { deleted: boolean; has_controller: boolean; controller_kind: string } }>(
        `${BASE}/pods/${id}/restart`
      )
      .then((r) => r.data.data),

  podLogsWsURL: (id: string, tail = 200, follow = true) =>
    wsUrlFor(`${BASE}/pods/${id}/logs?tail=${tail}&follow=${follow}`),

  podTerminalWsURL: (id: string) => wsUrlFor(`${BASE}/pods/${id}/terminal`),

  listHelm: () =>
    platformApi
      .get<{ data: AssignedHelmRelease[]; meta: { total: number } }>(`${BASE}/helm-releases`)
      .then((r) => r.data),

  getHelm: (id: string) =>
    platformApi
      .get<{ data: AssignedHelmRelease; live: any; pods: PodSummary[] }>(
        `${BASE}/helm-releases/${id}`
      )
      .then((r) => r.data),

  helmLogsWsURL: (
    id: string,
    podName: string,
    containerName?: string,
    tail = 200,
    follow = true
  ) => {
    let qs = `pod_name=${encodeURIComponent(podName)}&tail=${tail}&follow=${follow}`;
    if (containerName) qs += `&container_name=${encodeURIComponent(containerName)}`;
    return wsUrlFor(`${BASE}/helm-releases/${id}/logs?${qs}`);
  },

  helmTerminalWsURL: (id: string, podName: string, containerName?: string) => {
    let qs = `pod_name=${encodeURIComponent(podName)}`;
    if (containerName) qs += `&container_name=${encodeURIComponent(containerName)}`;
    return wsUrlFor(`${BASE}/helm-releases/${id}/terminal?${qs}`);
  },

  uninstallHelm: (id: string) =>
    platformApi
      .delete<{ data: { already_removed: boolean } }>(`${BASE}/helm-releases/${id}`)
      .then((r) => r.data.data),
};
